import { environment } from '@/stores/environment';

/**
 * Serviço centralizado para gerenciamento de autenticação
 * Controla se os dados são salvos em localStorage ou sessionStorage
 * baseado na opção "manter-se conectado"
 */
export const authService = {
  
  /**
   * Salva os dados de autenticação do usuário
   * @param {Object} authData - Dados de autenticação
   * @param {string} authData.token - Token de acesso
   * @param {string} authData.user - Nome do usuário
   * @param {string} authData.userId - ID do usuário
   * @param {boolean} rememberMe - Se deve manter conectado
   */
  setAuthData(authData, rememberMe = false) {
    const storage = rememberMe ? localStorage : sessionStorage;
    
    // Salva os dados de autenticação
    storage.setItem('user_token', authData.token);
    storage.setItem('user', authData.user);
    storage.setItem('userId', authData.userId);
    
    // Salva a preferência de "manter conectado"
    localStorage.setItem('remember_me', rememberMe.toString());
    
    // Atualiza o token global
    environment.API_TOKEN = authData.token;
    
    // Se não é para lembrar, limpa dados do localStorage
    if (!rememberMe) {
      localStorage.removeItem('user_token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
    }
  },

  /**
   * Recupera os dados de autenticação
   * Verifica primeiro localStorage, depois sessionStorage
   * @returns {Object|null} Dados de autenticação ou null se não encontrar
   */
  getAuthData() {
    // Verifica se tem preferência de "manter conectado"
    const rememberMe = localStorage.getItem('remember_me') === 'true';
    
    let token, user, userId;
    
    if (rememberMe) {
      // Se tem "lembrar", busca no localStorage
      token = localStorage.getItem('user_token');
      user = localStorage.getItem('user');
      userId = localStorage.getItem('userId');
    } else {
      // Senão, busca no sessionStorage
      token = sessionStorage.getItem('user_token');
      user = sessionStorage.getItem('user');
      userId = sessionStorage.getItem('userId');
    }
    
    if (token && user && userId) {
      // Atualiza o token global
      environment.API_TOKEN = token;
      
      return {
        token,
        user,
        userId,
        rememberMe
      };
    }
    
    return null;
  },

  /**
   * Verifica se o usuário está logado
   * @returns {boolean} True se estiver logado
   */
  isLoggedIn() {
    const authData = this.getAuthData();
    return authData !== null;
  },

  /**
   * Obtém o token de autenticação
   * @returns {string|null} Token ou null se não estiver logado
   */
  getToken() {
    const authData = this.getAuthData();
    return authData ? authData.token : null;
  },

  /**
   * Obtém os dados do usuário logado
   * @returns {Object|null} Dados do usuário ou null se não estiver logado
   */
  getUserData() {
    const authData = this.getAuthData();
    return authData ? {
      user: authData.user,
      userId: authData.userId
    } : null;
  },

  /**
   * Remove todos os dados de autenticação (logout)
   */
  clearAuthData() {
    // Remove de ambos os storages
    localStorage.removeItem('user_token');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('remember_me');
    
    sessionStorage.removeItem('user_token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('userId');
    
    // Volta para o token padrão
    environment.API_TOKEN = environment.API_TOKEN_DEFAULT;
  },

  /**
   * Inicializa o serviço de autenticação
   * Deve ser chamado na inicialização da aplicação
   */
  initialize() {
    const authData = this.getAuthData();
    if (authData) {
      // Se tem dados válidos, atualiza o token global
      environment.API_TOKEN = authData.token;
      console.log('Usuário autenticado automaticamente:', authData.user);
    } else {
      // Se não tem dados, usa token padrão
      environment.API_TOKEN = environment.API_TOKEN_DEFAULT;
    }
  }
};
